import { useState, useMemo, useEffect } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Search, Filter, Grid, List, ArrowLeft } from "lucide-react";
import { useCart } from "@/App";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ProductCard from "@/components/ProductCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function ProductsPage() {
  const { categorySlug } = useParams();
  const [searchParams] = useSearchParams();
  const showSpecials = searchParams.get("special") === "true";
  const urlSearch = searchParams.get("search") || "";
  
  const { categories, products, loading } = useCart();
  const [searchTerm, setSearchTerm] = useState(urlSearch);

  useEffect(() => {
    setSearchTerm(urlSearch);
  }, [urlSearch]);
  const [sortBy, setSortBy] = useState("name");
  // Sub-category chip filter (currently only used by Toiletries category).
  // Reset whenever the user navigates to a different category.
  const [subFilter, setSubFilter] = useState("all");
  useEffect(() => {
    setSubFilter("all");
  }, [categorySlug]);

  // Pagination — page size chosen to keep the DOM ~24 cards initially and
  // avoid rendering all 1,400+ products at once (browser overload / 429s).
  const PAGE_SIZE = 24;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  // Reset pagination when filters/search/sort change
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [categorySlug, showSpecials, searchTerm, sortBy, subFilter]);

  const currentCategory = categorySlug 
    ? categories.find((c) => c.slug === categorySlug) 
    : null;

  // Product count per category (excludes unavailable) for the sidebar aisle counts.
  const categoryCounts = useMemo(() => {
    const counts = {};
    let total = 0;
    for (const p of products) {
      if (p.is_available === false) continue;
      total += 1;
      counts[p.category_id] = (counts[p.category_id] || 0) + 1;
    }
    counts.__all__ = total;
    counts.__specials__ = products.filter(
      (p) => p.is_special && p.is_available !== false
    ).length;
    return counts;
  }, [products]);

  // Sub-category definitions per category. Each entry maps a chip key → regex
  // matched against product name + description. Order = chip order in the UI.
  // Only categories listed here render the chip row.
  const SUB_CATEGORY_FILTERS = {
    toiletries: [
      { key: "all", label: "All", re: null },
      { key: "skin-care", label: "Skin Care", re: /\b(lotion|cream|body butter|skin|brightening|lightening|beauty milk)\b/i },
      { key: "soap", label: "Soap", re: /\b(soap|black soap|cleansing bar)\b/i },
      { key: "oral-care", label: "Oral Care", re: /\b(toothpaste|toothbrush|tooth)\b/i },
      { key: "pain-relief", label: "Pain Relief", re: /\b(balm|ointment|pain relief|antiseptic|medicated)\b/i },
    ],
    "off-licence": [
      { key: "all", label: "All", re: null },
      { key: "beer-stout", label: "Beer & Stout", re: /\b(beer|lager|stout|ale|cider|guinness|heineken|carlsberg|peroni|corona|stella|budweiser|kronenbourg|gulder|trophy|star lager)\b/i },
      { key: "wine", label: "Wine", re: /\b(wine|merlot|cabernet|chardonnay|ros[eé]|sauvignon|pinot|prosecco|champagne|moscato|riesling|shiraz|malbec|sparkling)\b/i },
      { key: "bitters-spirits", label: "Bitters & Spirits", re: /\b(bitters?|whisky|whiskey|scotch|bourbon|vodka|gin|rum|brandy|cognac|liqueur|spirit)\b/i },
    ],
    hair: [
      { key: "all", label: "All", re: null },
      { key: "extensions", label: "Extensions & Braids", re: /\b(braid|extension|kinky bulk|x-pression|crochet)\b/i },
      { key: "conditioner", label: "Conditioner", re: /\b(conditioner)\b/i },
      { key: "relaxer", label: "Relaxer", re: /\b(relaxer|texturizer|texturiser)\b/i },
      { key: "styling-gel", label: "Styling Gel", re: /\bstyling gel\b/i },
      { key: "treatment-oil", label: "Treatment & Oil", re: /\b(treatment|fertilizer|hair food|hair oil)\b/i },
      { key: "hairspray", label: "Hairspray", re: /\bhairspray\b/i },
    ],
    drinks: [
      { key: "all", label: "All", re: null },
      { key: "soft-drinks", label: "Soft Drinks", re: /\b(coke|cola|pepsi|fanta|sprite|7up|7\s?up|lemonade|squash|cordial|punch|sparkling|fizzy|soft drink)\b/i },
      { key: "juice", label: "Juice", re: /\b(juice|nectar|smoothie)\b/i },
      { key: "malt", label: "Malt", re: /\b(malt|maltina|amstel)\b/i },
      { key: "energy", label: "Energy", re: /\b(energy|red bull|monster|lucozade|boost)\b/i },
      { key: "water", label: "Water", re: /\b(water|spring water|mineral water|still water)\b/i },
    ],
    spices: [
      { key: "all", label: "All", re: null },
      { key: "seasoning", label: "Seasoning", re: /\bseasoning\b/i },
      { key: "ground-powder", label: "Ground & Powder", re: /\b(powder|ground|pulverized)\b/i },
      { key: "whole-leaves", label: "Whole & Leaves", re: /\b(seeds?|leaves|leaf|sticks?|whole|hibiscus|bay|rosemary|cumin|coriander seeds|cloves?)\b/i },
      { key: "stock-bouillon", label: "Stock & Bouillon", re: /\b(stock|bouillon|cube|cubes|sachet|shito|knorr|maggi)\b/i },
      { key: "pepper-chilli", label: "Pepper & Chilli", re: /\b(pepper|chilli|chili|cayenne|paprika|chile)\b/i },
      { key: "salt", label: "Salt", re: /\b(salt|sea salt|cooking salt)\b/i },
      { key: "curry", label: "Curry", re: /\b(curry|garam masala|jollof|pepper soup|jerk)\b/i },
      { key: "soy-sauce", label: "Sauces & Vinegar", re: /\b(soy sauce|vinegar|fish sauce|hot sauce|mustard)\b/i },
    ],
    household: [
      { key: "all", label: "All", re: null },
      { key: "cleaning", label: "Cleaning", re: /\b(cleaner|stain remover|laundry|detergent|scourer|mop|bleach|disinfect|cloth|wipe|window|kitchen cleaner|soap)\b/i },
      { key: "catering-disposables", label: "Catering Disposables", re: /\b(plate|fork|spoon|knife|cup|bowl|cutlery|napkin|tissue)\b/i },
      { key: "foil-wrap-bags", label: "Foil, Wrap & Bags", re: /\b(foil container|foil|cling film|baking paper|paper|bag|wrap)\b/i },
      { key: "cookware", label: "Cookware", re: /\b(pressure cooker|cooker|set|pan|pot|cast|ceramic|marble|chopping board|scissors|peeler|whisk)\b/i },
      { key: "storage", label: "Storage", re: /\b(storage container|container|jar|bottle|tupperware)\b/i },
      { key: "candles", label: "Candles & Misc", re: /\b(candle|memorial|lighter|battery)\b/i },
    ],
    "flours-powdered-foods": [
      { key: "all", label: "All", re: null },
      { key: "yam-fufu", label: "Yam & Fufu", re: /\b(yam|poundo|fufu|garri|gari|cocoyam)\b/i },
      { key: "semolina-maize", label: "Semolina & Maize", re: /\b(semolina|semovita|maize|corn|hominy)\b/i },
      { key: "wheat-atta", label: "Wheat & Atta", re: /\b(atta|chapatti|chapati|wheat|plain flour|self-raising)\b/i },
      { key: "banku-tz", label: "Banku & TZ", re: /\b(banku|tuo zaafi|hausa koko|akamu|ogi|pap)\b/i },
      { key: "beans-flour", label: "Beans Flour", re: /\bbeans flour\b/i },
      { key: "plantain-cassava", label: "Plantain & Cassava", re: /\b(plantain flour|cassava flour|cassava)\b/i },
    ],
    "tinned-foods": [
      { key: "all", label: "All", re: null },
      { key: "mackerel-sardines", label: "Mackerel & Sardines", re: /\b(mackerel|sardines?|tuna|pilchard)\b/i },
      { key: "tomato", label: "Tomato", re: /\b(tomato|ketchup|puree)\b/i },
      { key: "soup-base", label: "Soup Bases", re: /\b(palm nut|banga|abemudro|pepper soup|soup)\b/i },
      { key: "beans-peas", label: "Beans & Peas", re: /\b(kidney beans?|chickpeas?|peas|garden peas|bean|cowpea)\b/i },
      { key: "corned-beef", label: "Corned Beef", re: /\bcorned beef\b/i },
      { key: "veg-leaves", label: "Veg & Leaves", re: /\b(garden egg|aubergine|cocoyam|spinach|leaves)\b/i },
    ],
    beverages: [
      { key: "all", label: "All", re: null },
      { key: "custard", label: "Custard", re: /\bcustard\b/i },
      { key: "tea-coffee", label: "Tea & Coffee", re: /\b(tea|teabags|coffee|cocoa|chocolate|herbal|infusion)\b/i },
      { key: "pap-ogi", label: "Pap & Ogi", re: /\b(ogi|pap|koko|akamu)\b/i },
      { key: "milk", label: "Milk", re: /\b(milk|evaporated|condensed)\b/i },
      { key: "peanut-butter", label: "Peanut Butter", re: /\bpeanut butter\b/i },
      { key: "baking", label: "Baking", re: /\b(baking powder|bicarbonate|bicarb|gram flour|plain flour|self-raising|yeast)\b/i },
    ],
    "frozen-meat": [
      { key: "all", label: "All", re: null },
      { key: "beef", label: "Beef", re: /\b(beef|cow|abodi|reed|ponmo|pomo|shaki|tripe|brisket|tendon|ishan)\b/i },
      { key: "offal", label: "Offal", re: /\b(liver|kidney|lungs|gizzard|mask|snout|tongue|heart|tail)\b/i },
      { key: "feet-foot", label: "Feet & Foot", re: /\b(feet|foot|paya|trotter)\b/i },
      { key: "lamb-goat", label: "Lamb & Goat", re: /\b(lamb|goat|mutton)\b/i },
      { key: "boxes-bulk", label: "Boxes & Bulk", re: /\b(box|bulk|case|carton|wholesale|10kg|20kg)\b/i },
    ],
    "halal-meat": [
      { key: "all", label: "All", re: null },
      { key: "beef", label: "Beef", re: /\b(beef|cow|abodi|reed|ponmo|shaki|tripe|brisket|tendon|ishan|ox)\b/i },
      { key: "chicken", label: "Chicken", re: /\b(chicken|hen|drumstick|gizzard|wing|leg|lap|breast|thigh)\b/i },
      { key: "lamb-goat", label: "Lamb & Goat", re: /\b(lamb|goat|mutton)\b/i },
      { key: "turkey", label: "Turkey", re: /\bturkey\b/i },
      { key: "offal", label: "Offal", re: /\b(liver|kidney|lungs|gizzard|mask|snout|tongue|heart|tail)\b/i },
    ],
    "snacks-nuts": [
      { key: "all", label: "All", re: null },
      { key: "chin-chin", label: "Chin-Chin", re: /\bchin[\s-]?chin\b/i },
      { key: "plantain-chips", label: "Plantain Chips", re: /\bplantain (chips?|crisps?)\b/i },
      { key: "peanuts-nuts", label: "Peanuts & Nuts", re: /\b(peanut|groundnut|cashew|almond|nut)\b/i },
      { key: "biscuits-cakes", label: "Biscuits & Cakes", re: /\b(biscuit|cake|hobnobs|digestive|fairy|rich tea|ginger nut|trancetto)\b/i },
      { key: "roasted", label: "Roasted", re: /\b(roasted|ukwa|uzuaku|popcorn)\b/i },
    ],
    "fruits-and-vegetables": [
      { key: "all", label: "All", re: null },
      { key: "fresh-fruits", label: "Fresh Fruits", re: /\b(apple|orange|banana|mango|pawpaw|papaya|pear|lime|lemon|tangerine|coconut|grape|melon|watermelon|safou|ube)\b/i },
      { key: "peppers-chilli", label: "Peppers & Chilli", re: /\b(pepper|chilli|chili|scotch bonnet)\b/i },
      { key: "leafy-greens", label: "Leafy Greens", re: /\b(spinach|parsley|coriander|mint|cabbage|lettuce|leaf|leaves)\b/i },
      { key: "root-tubers", label: "Root & Tubers", re: /\b(potato|cassava|yam|sweet potato|cocoyam|carrot|garlic|ginger|onion)\b/i },
      { key: "plantain-bananas", label: "Plantain & Bananas", re: /\b(plantain|matoke|banana)\b/i },
      { key: "tomatoes", label: "Tomatoes", re: /\btomato\b/i },
    ],
    oil: [
      { key: "all", label: "All", re: null },
      { key: "palm-oil", label: "Palm Oil", re: /\b(palm oil|banga|zomi)\b/i },
      { key: "vegetable-cooking", label: "Vegetable & Cooking", re: /\b(vegetable oil|cooking oil|sunflower|rapeseed)\b/i },
      { key: "coconut-oil", label: "Coconut Oil", re: /\b(coconut oil|creamed coconut)\b/i },
      { key: "speciality", label: "Speciality", re: /\b(sesame|almond|olive oil|peanut oil|argan)\b/i },
      { key: "ghee", label: "Ghee & Butter", re: /\b(ghee|butter)\b/i },
    ],
    beans: [
      { key: "all", label: "All", re: null },
      { key: "honey-brown", label: "Honey & Brown", re: /\b(honey beans|brown beans|peeled beans|oloyin)\b/i },
      { key: "blackeye-cowpea", label: "Black-Eye & Cow Pea", re: /\b(black[\s-]?eye|cow pea|cowpea|chori|crabeye|gungo)\b/i },
      { key: "kidney-beans", label: "Kidney Beans", re: /\b(kidney beans|rajma)\b/i },
      { key: "lentils-dal", label: "Lentils & Dal", re: /\b(dal|dall|urid|urad|moong|moth|chana|toor|masoor)\b/i },
      { key: "peanuts", label: "Peanuts", re: /\b(peanut|groundnut)\b/i },
      { key: "black-beans", label: "Black Beans", re: /\bblack beans\b/i },
    ],
    "frozen-fish": [
      { key: "all", label: "All", re: null },
      { key: "mackerel", label: "Mackerel", re: /\bmackerel\b/i },
      { key: "tilapia", label: "Tilapia", re: /\btilapia\b/i },
      { key: "catfish", label: "Catfish", re: /\bcatfish\b/i },
      { key: "croaker", label: "Croaker", re: /\bcroaker\b/i },
      { key: "hake-pangasius", label: "Hake & Pangasius", re: /\b(hake|pangasius)\b/i },
      { key: "shrimp-crab", label: "Shrimp & Crab", re: /\b(shrimp|prawn|crab|seafood)\b/i },
      { key: "boxes", label: "Boxes & Bulk", re: /\b(box|bulk|case|carton|wholesale)\b/i },
    ],
    "frozen-poultry": [
      { key: "all", label: "All", re: null },
      { key: "whole-chicken", label: "Whole Chicken", re: /\b(whole chicken|baby chicken|hen|hard chicken)\b/i },
      { key: "chicken-parts", label: "Chicken Parts", re: /\b(drumstick|gizzard|leg|lap|wing|feet|laps?)\b/i },
      { key: "turkey", label: "Turkey", re: /\bturkey\b/i },
      { key: "smoked", label: "Smoked", re: /\bsmoked\b/i },
      { key: "boxes", label: "Boxes & Bulk", re: /\b(box|bulk|case|carton|wholesale|10kg|20kg)\b/i },
    ],
    "frozen-vegetables": [
      { key: "all", label: "All", re: null },
      { key: "leaves-greens", label: "Leaves & Greens", re: /\b(leaves|leaf|callalou|callaloo|ndole|fumbwa|utazi|ugu|sour|ngai|spinach|amaranth|matembele|folon|liboka|pondu|biteku)\b/i },
      { key: "cassava-fufu", label: "Cassava & Fufu", re: /\b(cassava|fufu|attieke|miondo|bobolo|water fufu|manioc)\b/i },
      { key: "okra-mulukhia", label: "Okra & Mulukhia", re: /\b(okra|mulukhia|molokhia)\b/i },
      { key: "frankfurters", label: "Frankfurters", re: /\b(frankfurter|frank|sausage)\b/i },
      { key: "mixed-veg", label: "Mixed Veg & Peas", re: /\b(mixed vegetables|peas|sweet corn|garlic|ginger|onion)\b/i },
    ],
    "dried-fish": [
      { key: "all", label: "All", re: null },
      { key: "stockfish", label: "Stockfish", re: /\bstockfish\b/i },
      { key: "catfish", label: "Catfish", re: /\bcatfish\b/i },
      { key: "bonga", label: "Bonga", re: /\bbonga\b/i },
      { key: "crayfish-shrimp", label: "Crayfish & Shrimp", re: /\b(crayfish|shrimp|prawn|periwinkle)\b/i },
      { key: "barracuda", label: "Other", re: /\b(barracuda|cod|tusk)\b/i },
    ],
    "corn-lentils": [
      { key: "all", label: "All", re: null },
      { key: "corn", label: "Corn", re: /\bcorn\b/i },
      { key: "lentils-dal", label: "Lentils & Dal", re: /\b(dal|dall|lentil|toor|moong|urid|chana|masoor)\b/i },
    ],
    "rice-grains": [
      { key: "all", label: "All", re: null },
      { key: "basmati", label: "Basmati", re: /\bbasmati\b/i },
      { key: "long-grain", label: "Long Grain", re: /\b(long grain|easy cook|parboiled)\b/i },
      { key: "jollof", label: "Jollof", re: /\bjollof\b/i },
      { key: "broken", label: "Broken Rice", re: /\bbroken\b/i },
      { key: "ofada-brown", label: "Ofada & Brown", re: /\b(ofada|brown rice|wild rice|black rice)\b/i },
    ],
  };
  const subFilterChips = currentCategory ? SUB_CATEGORY_FILTERS[currentCategory.slug] : null;

  const filteredProducts = useMemo(() => {
    let filtered = products;

    // Filter by category
    if (currentCategory) {
      filtered = filtered.filter((p) => p.category_id === currentCategory.id);
    }

    // Filter specials
    if (showSpecials) {
      filtered = filtered.filter((p) => p.is_special);
    }

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.name.toLowerCase().includes(term) ||
          p.description?.toLowerCase().includes(term)
      );
    }

    // Sub-category chip filter (e.g. Toiletries → Hair Care).
    if (subFilterChips && subFilter !== "all") {
      const chip = subFilterChips.find((c) => c.key === subFilter);
      if (chip?.re) {
        filtered = filtered.filter((p) =>
          chip.re.test(`${p.name} ${p.description || ""}`)
        );
      }
    }

    // Sort
    filtered = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case "price-low":
          return (a.special_price || a.price) - (b.special_price || b.price);
        case "price-high":
          return (b.special_price || b.price) - (a.special_price || a.price);
        case "name":
        default:
          return a.name.localeCompare(b.name);
      }
    });

    return filtered;
  }, [products, currentCategory, showSpecials, searchTerm, sortBy, subFilter, subFilterChips]);

  const pageTitle = urlSearch
    ? `Search: "${urlSearch}"`
    : showSpecials 
      ? "Weekly Specials" 
      : currentCategory?.name || "All Products";

  return (
    <div className="min-h-screen bg-white" data-testid="products-page">
      <Header />

      {/* Page Header — uses category banner as background when available */}
      <div
        className={
          currentCategory?.image_url
            ? "relative bg-slate-900 border-b overflow-hidden"
            : "bg-slate-50 border-b"
        }
      >
        {currentCategory?.image_url && (
          <img
              src={currentCategory.image_url}
              alt={currentCategory.name}
              className="absolute right-4 sm:right-6 top-1/2 -translate-y-1/2 max-h-[80%] max-w-[55%] object-contain rounded-lg shadow-lg"
              data-testid="category-hero-image"
            />
        )}
        <div
          className={`relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${
            currentCategory?.image_url ? "py-12 sm:py-16" : "py-8"
          }`}
        >
          {/* Breadcrumb */}
          <div
            className={`flex items-center gap-2 text-sm mb-4 ${
              currentCategory?.image_url ? "text-white/70" : "text-slate-500"
            }`}
          >
            <Link
              to="/"
              className={
                currentCategory?.image_url
                  ? "hover:text-white"
                  : "hover:text-emerald-600"
              }
            >
              Home
            </Link>
            <span>/</span>
            <Link
              to="/products"
              className={
                currentCategory?.image_url
                  ? "hover:text-white"
                  : "hover:text-emerald-600"
              }
            >
              Shop
            </Link>
            {(currentCategory || showSpecials) && (
              <>
                <span>/</span>
                <span
                  className={
                    currentCategory?.image_url ? "text-white" : "text-slate-900"
                  }
                >
                  {pageTitle}
                </span>
              </>
            )}
          </div>

          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1
                className={`text-2xl sm:text-4xl font-bold ${
                  currentCategory?.image_url ? "text-white" : "text-slate-900"
                }`}
              >
                {pageTitle}
              </h1>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar - Categories */}
          <aside className="lg:w-64 flex-shrink-0">
            <div className="sticky top-24 space-y-6">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>

              {/* Categories */}
              <div className="bg-white rounded-xl border p-4">
                <h3 className="font-semibold text-slate-900 mb-3">Categories</h3>
                <div className="space-y-1">
                  <Link
                    to="/products"
                    className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                      !categorySlug && !showSpecials
                        ? "bg-emerald-50 text-emerald-700 font-medium"
                        : "text-slate-600 hover:bg-slate-50"
                    }`}
                    data-testid="filter-all"
                  >
                    <span>All Products</span>
                    <span
                      className={`text-xs font-semibold ${
                        !categorySlug && !showSpecials
                          ? "text-emerald-600"
                          : "text-slate-400"
                      }`}
                      data-testid="filter-count-all"
                    >
                      {categoryCounts.__all__ || 0}
                    </span>
                  </Link>
                  {categories.map((cat) => {
                    const active = categorySlug === cat.slug;
                    const count = categoryCounts[cat.id] || 0;
                    return (
                      <Link
                        key={cat.id}
                        to={`/products/${cat.slug}`}
                        className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                          active
                            ? "bg-emerald-50 text-emerald-700 font-medium"
                            : "text-slate-600 hover:bg-slate-50"
                        }`}
                        data-testid={`filter-${cat.slug}`}
                      >
                        <span className="truncate">{cat.name}</span>
                        <span
                          className={`text-xs font-semibold flex-shrink-0 ${
                            active ? "text-emerald-600" : "text-slate-400"
                          }`}
                          data-testid={`filter-count-${cat.slug}`}
                        >
                          {count}
                        </span>
                      </Link>
                    );
                  })}
                  <Link
                    to="/products?special=true"
                    className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                      showSpecials
                        ? "bg-red-50 text-red-700 font-medium"
                        : "text-red-600 hover:bg-red-50"
                    }`}
                    data-testid="filter-specials"
                  >
                    <span className="flex items-center">
                      <Badge variant="outline" className="border-red-200 text-red-600 mr-2">
                        Sale
                      </Badge>
                      Weekly Specials
                    </span>
                    <span
                      className={`text-xs font-semibold ${
                        showSpecials ? "text-red-600" : "text-red-400"
                      }`}
                      data-testid="filter-count-specials"
                    >
                      {categoryCounts.__specials__ || 0}
                    </span>
                  </Link>
                </div>
              </div>

              {/* Sort - Mobile hidden, shown in header on mobile */}
              <div className="hidden lg:block bg-white rounded-xl border p-4">
                <h3 className="font-semibold text-slate-900 mb-3">Sort By</h3>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger data-testid="sort-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="name">Name (A-Z)</SelectItem>
                    <SelectItem value="price-low">Price: Low to High</SelectItem>
                    <SelectItem value="price-high">Price: High to Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1">
            {/* Sub-category chip row (Toiletries etc.) — auto-hides chips with 0 hits, shows count badges */}
            {subFilterChips && (
              <div
                className="mb-6 flex flex-wrap gap-2"
                data-testid="subcategory-chips"
              >
                {subFilterChips
                  .map((chip) => {
                    if (!chip.re || !currentCategory) {
                      // "All" chip — show full category count
                      const count = currentCategory
                        ? products.filter(
                            (p) =>
                              p.category_id === currentCategory.id &&
                              p.is_available !== false
                          ).length
                        : 0;
                      return { ...chip, count };
                    }
                    const count = products.filter(
                      (p) =>
                        p.category_id === currentCategory.id &&
                        p.is_available !== false &&
                        chip.re.test(`${p.name} ${p.description || ""}`)
                    ).length;
                    return { ...chip, count };
                  })
                  .filter((chip) => chip.count > 0)
                  .map((chip) => {
                    const active = subFilter === chip.key;
                    return (
                      <button
                        key={chip.key}
                        type="button"
                        onClick={() => setSubFilter(chip.key)}
                        className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors flex items-center gap-1.5 ${
                          active
                            ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                            : "border-slate-200 text-slate-700 hover:border-emerald-500 hover:bg-emerald-50 hover:text-emerald-700"
                        }`}
                        data-testid={`subchip-${chip.key}`}
                      >
                        <span>{chip.label}</span>
                        <span
                          className={`text-xs font-semibold ${
                            active ? "text-emerald-600" : "text-slate-400"
                          }`}
                        >
                          {chip.count}
                        </span>
                      </button>
                    );
                  })}
              </div>
            )}

            {/* Mobile sort */}
            <div className="lg:hidden mb-6">
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-full" data-testid="mobile-sort-select">
                  <SelectValue placeholder="Sort by..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="name">Name (A-Z)</SelectItem>
                  <SelectItem value="price-low">Price: Low to High</SelectItem>
                  <SelectItem value="price-high">Price: High to Low</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="space-y-4">
                    <Skeleton className="h-48 rounded-xl" />
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-4 w-1/2" />
                  </div>
                ))}
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Search className="h-10 w-10 text-slate-400" />
                </div>
                <h3 className="text-lg font-medium text-slate-900 mb-2">No products found</h3>
                <p className="text-slate-500 mb-6">Try adjusting your search or filters</p>
                <Link to="/products">
                  <Button variant="outline">
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    View all products
                  </Button>
                </Link>
              </div>
            ) : (
              <>
                {/* Product count / pagination status */}
                <div
                  className="mb-4 text-sm text-slate-600"
                  data-testid="products-count"
                >
                  Showing{" "}
                  <span className="font-semibold text-slate-900">
                    {Math.min(visibleCount, filteredProducts.length)}
                  </span>{" "}
                  of{" "}
                  <span className="font-semibold text-slate-900">
                    {filteredProducts.length}
                  </span>{" "}
                  {filteredProducts.length === 1 ? "product" : "products"}
                </div>

                <div
                  className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6"
                  data-testid="products-grid"
                >
                  {filteredProducts.slice(0, visibleCount).map((product) => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>

                {visibleCount < filteredProducts.length && (
                  <div className="flex justify-center mt-10">
                    <Button
                      onClick={() =>
                        setVisibleCount((c) => c + PAGE_SIZE)
                      }
                      size="lg"
                      variant="outline"
                      className="border-emerald-500 text-emerald-700 hover:bg-emerald-50 rounded-full px-8"
                      data-testid="load-more-btn"
                    >
                      Load More (
                      {Math.min(
                        PAGE_SIZE,
                        filteredProducts.length - visibleCount
                      )}{" "}
                      more)
                    </Button>
                  </div>
                )}
              </>
            )}
          </main>
        </div>
      </div>

      <Footer />
    </div>
  );
}
